import React from 'react';
import RequiredReasonDialog from './RequiredReasonDialog';
import {
  SKIP_APPROVALS_TITLE,
  SKIP_APPROVALS_PROMPT_LABEL,
  skipApprovalsMessage,
  skipApprovalsConfirmLabel,
} from '../utils/skipApprovalsWording';

/**
 * THE reason dialog in front of skipping a pending approval — both doors.
 *
 * Owner, 2026-08-19: forcing an order through must *"make the jump demand a
 * reason"*; 2026-09-13: *"the override should require a reason"*. See
 * `utils/skipApprovalsWording.js` for the copy deck and for why the two doors
 * (the `Skip remaining approvals…` menu item and the `Override order` status
 * dropdown) share one wording.
 *
 * 🔑 **GATE, NOT DECORATION — AND THE SERVER IS THE REAL ONE.** Callers must
 * not issue the request until `onConfirm` fires, but neither route trusts them
 * to: `POST /api/approvals/orders/:id/override` answers
 * `400 override_reason_required` and `PATCH /api/orders/:id/status` answers
 * `400 status_jump_reason_required`. This is the UI that makes the refusal
 * unnecessary, exactly as `ApproveOnBehalfDialog.js` is for its ruling.
 *
 * 🔑 **`variant="danger"` LIVES HERE, NOT ON THE MENU ITEM.** The item itself
 * is amber (`tone: 'progress'`) because red is already Reject's colour two rows
 * above it in the same panel — see the `override` descriptor in
 * `constants/orderLifecycleMenuItems.js` for that argument in full. The red
 * belongs on the confirmation, where there is nothing to be confused with.
 *
 * 🚨 **THE PARENT OWNS THE REASON STATE, AND THAT IS NOT AN OVERSIGHT.**
 * `ConfirmDialog`'s `promptValue`/`onPromptChange` is a CONTROLLED textarea, so
 * the value has to live above it; holding it here would make this component
 * stateful and would lose the text on any parent re-render that remounted it.
 * Both callers keep it beside the prompt object they already hold.
 *
 * 🪤 **RENDER IT AT PAGE LEVEL, NEVER INSIDE THE ACTIONS MENU.**
 * `ConfirmDialog` is a plain `fixed inset-0` overlay with NO portal, and
 * `ui/Dropdown` closes on an outside `mousedown` — so a dialog mounted inside
 * the menu panel would be unmounted by the first click the user made in it.
 * That is why `InlineStatusJump`, which renders inside that panel, asks its
 * PARENT for a reason through a callback instead of rendering this itself.
 */
export default function SkipApprovalsDialog({
  open,
  orderNumber,
  approvalInfo,
  targetStatus,
  reason,
  onReasonChange,
  busy,
  onConfirm,
  onCancel,
}) {
  return (
    <RequiredReasonDialog
      open={open}
      variant="danger"
      title={SKIP_APPROVALS_TITLE}
      message={skipApprovalsMessage({ orderNumber, approvalInfo, targetStatus })}
      reasonLabel={SKIP_APPROVALS_PROMPT_LABEL}
      reason={reason}
      onReasonChange={onReasonChange}
      // 🔑 THE CLIENT HALF OF THE RULING — Confirm stays disabled until the
      // TRIMMED reason is non-empty. 🔀 Since 2026-09-14 that gate lives ONCE,
      // in `RequiredReasonDialog`, shared with `ApproveOnBehalfDialog`: a
      // textarea full of spaces is exactly the empty reason the server
      // refuses, and two copies of the test is how one of them drifts to
      // truthiness.
      confirmLabel={skipApprovalsConfirmLabel(targetStatus)}
      // 🔑 NOT THE WORD "Cancel". `client/src/pages/CLAUDE.md` rules for this
      // dialog family that *"EVERY BUTTON SAYS WHAT IT DOES, AND 'CANCEL'
      // APPEARS NOWHERE"*, and the collision here is the same one that forced
      // `Keep editing` on the save-then-act prompt: Department Orders'
      // lifecycle contains a `cancel` ACTION, so a `Cancel` button on a dialog
      // about an order would mean two different things in one panel.
      cancelLabel="Keep the approvals"
      // ⚠️ THE SAFE OUTCOME TAKES FOCUS, and Escape / an overlay click both map
      // to it. This dialog bypasses a control; an instinctive Enter must not be
      // able to fire it. Same reasoning as the save-then-act prompt's.
      initialFocus="cancel"
      busy={busy}
      onConfirm={onConfirm}
      onCancel={onCancel}
    />
  );
}
